import { Document, Font, Page, pdf, StyleSheet, Text, View } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import i18next from 'i18next';
import { colors } from '../../constants/constants';

Font.register({
  family: 'Work Sans',
  src: 'https://fonts.gstatic.com/s/worksans/v2/ElUAY9q6T0Ayx4zWzW63VKCWcynf_cDxXwCLxiixG1c.ttf',
});

Font.register({
  family: 'Lato',
  src: 'https://fonts.gstatic.com/s/lato/v13/v0SdcGFAl2aezM9Vq_aFTQ.ttf',
});

// Create styles
const styles = StyleSheet.create({
  body: {
    padding: 0,
  },
  page: {
    padding: '1in',
  },
  boldText: {
    fontSize: 12,
    marginBottom: 5,
    fontWeight: 'bold',
    fontFamily: 'Lato',
  },
  criticalityAccent: {
    width: '16px',
    height: '100%',
    backgroundColor: 'red',
  },
  cardBase: {
    alignSelf: 'flex-start',
    marginBottom: '16px',
    borderRadius: '4px',
    width: '100%',
    border: '1px solid #C4C4C4',
  },
  accentBase: {
    width: 12,
    borderTopLeftRadius: '3px',
    borderBottomLeftRadius: '3px',
  },
  accentMajor: {
    backgroundColor: colors.major,
  },
  accentMinor: {
    backgroundColor: colors.minor,
  },
  accentInfo: {
    backgroundColor: colors.info,
  },
  horizontalDiviver: {
    width: '1px',
    borderRight: '1px solid #C4C4C4',
    marginRight: '16px',
  },
  cardText: {
    fontFamily: 'Lato',
    fontSize: 10,
    flexWrap: 'wrap',
  },
  cardTextWithBorder: {
    borderBottom: '1px solid #ccc',
    fontFamily: 'Lato',
    fontSize: 10,
    flexWrap: 'wrap',
    paddingBottom: 8,
  },
  cardTitle: {
    fontFamily: 'Work Sans',
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 4,
    marginTop: 5,
  },
  cardTitleLimited: {
    fontFamily: 'Work Sans',
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 4,
    maxWidth: '90%',
  },
  header: {
    fontFamily: 'Work Sans',
    fontSize: 18,
    marginBottom: 4,
    paddingBottom: 8,
  },
  inlineTag: {
    borderColor: 'green',
    borderRadius: 50,
    color: '#000',
    fontFamily: 'Lato',
    fontSize: 10,
    padding: '7px 10px',
    position: 'relative',
    marginRight: 5,
    marginTop: 5,
  },
  noFindingsCard: {
    alignSelf: 'flex-start',
    border: '1px solid #333333',
    borderRadius: 4,
    marginBottom: 12,
    padding: 15,
    width: '100%',
  },
  pageNumber: {
    bottom: '32px',
    fontFamily: 'Lato',
    fontSize: 12,
    color: '#666666',
    position: 'absolute',
    right: '32px',
  },
  subHeader: {
    fontFamily: 'Lato',
    fontSize: 13.5,
    marginBottom: 12,
  },
  tag: {
    borderColor: 'green',
    borderRadius: 50,
    color: '#000',
    fontFamily: 'Lato',
    fontSize: 10,
    padding: '7px 10px',
    position: 'absolute',
    right: 10,
    top: 10,
  },
  usualFindingTag: {
    borderWidth: '1px',
    borderColor: '#201547',
    borderRadius: 50,
    color: '#201547',
    fontFamily: 'Lato',
    fontSize: 10,
    padding: '5px 6px',
  },
  text: {
    fontFamily: 'Lato',
    fontSize: 12,
    marginBottom: 5,
    flexWrap: 'wrap',
  },
  title: {
    color: '#2EAF98',
    fontFamily: 'Work Sans',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 15,
    textAlign: 'left',
  },
});
function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function RenderAssessmentDetails({ assessmentDetails }) {
  const { auditees, auditors = '', scope, criteria, objectives } = assessmentDetails.content.details;

  return (
    <View style={styles.body}>
      <Text style={styles.header}>{i18next.t('assessment.pdf.assessmentDetails')}</Text>
      <Text style={styles.subHeader}>{i18next.t('assessment.pdf.auditees')}</Text>
      <Text style={[styles.text, { marginBottom: '16px' }]}>{auditees}</Text>
      <Text style={styles.subHeader}>{i18next.t('assessment.pdf.auditors')}</Text>
      <Text style={[styles.text, { marginBottom: '16px' }]}>{auditors}</Text>
      <Text style={styles.subHeader}>{i18next.t('assessment.pdf.auditScope')}</Text>
      <Text style={[styles.text, { marginBottom: '16px' }]}>{scope}</Text>
      <Text style={styles.subHeader}>{i18next.t('assessment.pdf.auditCriteria')}</Text>
      <Text style={[styles.text, { marginBottom: '16px' }]}>{criteria}</Text>
      <Text style={styles.subHeader}>{i18next.t('assessment.pdf.auditObjectives')}</Text>
      <Text style={[styles.text, { marginBottom: '16px' }]}>{objectives}</Text>
    </View>
  );
}

function CriticalityTag({ criticality, inline }) {
  const colorMap = {
    major: { backgroundColor: colors.major, color: '#000' },
    minor: { backgroundColor: colors.minor, color: '#000' },
    info: { backgroundColor: colors.info, color: '#000' },
    ok: { backgroundColor: 'white', color: 'green', borderColor: 'green', borderWidth: 1, borderStyle: 'solid' },
    default: { backgroundColor: 'gray', color: '#000' },
  };

  const style = colorMap[criticality] || colorMap.default;

  return (
    <View style={inline ? { ...styles.inlineTag, ...style } : { ...styles.tag, ...style }}>
      <Text>
        {criticality === 'ok' ? i18next.t('assessment.controlStatus.noFindings') : capitalizeFirstLetter(criticality)}
      </Text>
    </View>
  );
}

function UsualFindingTag({ textKey }) {
  return (
    <View style={styles.usualFindingTag}>
      <Text>{i18next.t(`finding.usualFinding.${textKey}`)}</Text>
    </View>
  );
}

function FindingCard({ finding }) {
  const colorMap = {
    major: styles.accentMajor,
    minor: styles.accentMinor,
    info: styles.accentInfo,
  };

  const criticalityStyle = colorMap[finding.criticality];

  return (
    <View style={[styles.cardBase, { flexDirection: 'row', width: '100%' }]}>
      <View style={[styles.accentBase, criticalityStyle]} />
      <View style={{ flexDirection: 'column', width: '100%', padding: '16px' }}>
        <CriticalityTag criticality={finding.criticality} />
        <View style={{ flexDirection: 'column', marginBottom: '16px' }}>
          <Text style={styles.cardTitleLimited}>{finding.controlTitle}</Text>
          <Text style={styles.cardText}>{finding.title}</Text>
          {finding.usualFindings.length > 0 && (
            <View style={{ flexDirection: 'row', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
              {finding.usualFindings.map((item) => (
                <UsualFindingTag textKey={item} />
              ))}
            </View>
          )}
        </View>
        <View style={{ flexDirection: 'row' }}>
          <View style={{ flex: 1, paddingRight: '16px' }}>
            <Text style={styles.cardTitle}>{i18next.t('assessment.pdf.findingDescription')}</Text>
            <Text style={styles.cardText}>{finding.description}</Text>
          </View>
          <View style={styles.horizontalDiviver} />
          <View style={{ flex: 1, paddingRight: '16px' }}>
            <Text style={styles.cardTitle}>{i18next.t('finding.recommendation')}</Text>
            <Text style={styles.cardText}>{finding.recommendation}</Text>
          </View>
          <View style={styles.horizontalDiviver} />
          <View style={{ flex: 1 }}>
            <Text style={styles.cardTitle}>{i18next.t('finding.managementResponse')}</Text>
            <Text style={styles.cardText}>{finding.response}</Text>
          </View>
        </View>
      </View>
    </View>
  );
}

function ListItem({ children }) {
  return (
    <View style={{ flexDirection: 'row' }}>
      <View style={{ height: '100%' }}>
        <Text style={styles.text}>{'\u2022 '}</Text>
      </View>
      <Text style={styles.text}>{children}</Text>
    </View>
  );
}

function RenderSummary({ controlCount, findings }) {
  const majorFindingsAmount = findings.filter((finding) => finding.criticality === 'major').length;
  const minorFindingsAmount = findings.filter((finding) => finding.criticality === 'minor').length;
  const infoFindingsAmount = findings.filter((finding) => finding.criticality === 'info').length;

  return (
    <View style={styles.body}>
      <Text style={styles.header}>{i18next.t('assessment.pdf.assessmentSummary')}</Text>
      <Text style={styles.subHeader}>{i18next.t('assessment.pdf.results')}</Text>
      <Text style={[styles.text, { marginBottom: '16px' }]}>
        {`${controlCount} ${i18next.t('assessment.findingList.totalControls')} `}
        {`${i18next.t('assessment.findingList.inScope')} `}
        {`${i18next.t('assessment.findingList.inTotal')} `}
        {`${findings.length} ${i18next.t('assessment.findingList.totalFindings')} `}
        {i18next.t('assessment.findingList.findingsMade')}
      </Text>
      <View style={{ marginBottom: '16px' }}>
        <ListItem>{`${majorFindingsAmount} ${i18next.t('assessment.pdf.majorFindings')} `}</ListItem>
        <ListItem>{`${minorFindingsAmount} ${i18next.t('assessment.pdf.minorFindings')} `}</ListItem>
        <ListItem>{`${infoFindingsAmount} ${i18next.t('assessment.pdf.infoFindings')} `}</ListItem>
      </View>
    </View>
  );
}

function RenderFindingsSummary({ findings, filterCriticality, titleKey }) {
  const filteredFindings = findings
    .filter((finding) => finding.criticality === filterCriticality)
    .map((finding) => ({
      title: finding.title,
      controlTitle: finding.controlTitle,
      description: finding.description,
      criticality: finding.criticality,
      recommendation: finding.recommendation,
      response: finding.response,
    }));

  const findingsCount = `(${filteredFindings.length})`;

  return (
    <View style={[styles.body, { marginBottom: '16px' }]}>
      {filteredFindings.map((finding, index) => (
        <View wrap={false} key={finding.id || index}>
          {index === 0 && (
            <Text style={styles.subHeader}>
              {i18next.t(`assessment.controlStatus.${titleKey}`)}
              {` ${findingsCount}`}
            </Text>
          )}
          <Text style={styles.text}>{`${finding.controlTitle} – ${finding.title}`}</Text>
        </View>
      ))}
    </View>
  );
}

function RenderFindings({ findings, filterCriticality, titleKey }) {
  // Filter findings based on criticality and map to a simpler object
  const filteredFindings = findings
    .filter((finding) => finding.criticality === filterCriticality)
    .map((finding) => ({
      title: finding.title,
      controlTitle: finding.controlTitle,
      description: finding.description,
      criticality: finding.criticality,
      recommendation: finding.recommendation,
      response: finding.response,
      usualFindings: finding.usualFindings,
    }));

  const findingsCount = `(${filteredFindings.length})`;

  return (
    <View style={styles.body}>
      {filteredFindings.map((finding, index) => (
        <View wrap={false} key={finding.id || index}>
          {index === 0 && (
            <Text style={styles.subHeader}>
              {i18next.t(`assessment.controlStatus.${titleKey}`)}
              {` ${findingsCount}`}
            </Text>
          )}
          <FindingCard finding={finding} />
        </View>
      ))}
    </View>
  );
}

function PDFFile({ assessmentDetails, findings, controlCount }) {
  const assessmentName = assessmentDetails.name;

  return (
    <Document>
      <Page size="a4" dpi={150} style={styles.page}>
        <Text style={styles.title}>{`${i18next.t('assessment.pdf.pdfTitle')} – ${assessmentName}`}</Text>
        <RenderAssessmentDetails
          assessmentDetails={assessmentDetails}
        />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
      <Page size="a4" dpi={150} style={styles.page}>
        <RenderSummary controlCount={controlCount} findings={findings} />
        <RenderFindingsSummary findings={findings} filterCriticality="major" titleKey="majorFindings" />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
      <Page size="a4" dpi={150} style={styles.page}>
        <Text style={styles.header}>{i18next.t('assessment.pdf.findings')}</Text>
        <RenderFindings
          controlCount={controlCount}
          findings={findings}
          filterCriticality="major"
          titleKey="majorFindings"
        />
        <RenderFindings
          controlCount={controlCount}
          findings={findings}
          filterCriticality="minor"
          titleKey="minorFindings"
        />
        <RenderFindings
          controlCount={controlCount}
          findings={findings}
          filterCriticality="info"
          titleKey="infoFindings"
        />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
}

export const downloadPdf = async ({ assessmentDetails, findings, controlCount }, callback) => {
  const document = PDFFile({ assessmentDetails, findings, controlCount });
  const blob = await pdf(document).toBlob();
  saveAs(blob, `${assessmentDetails.name}.pdf`);
  callback();
};

export default PDFFile;
